import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PatientForm } from '@/components/panels/PatientForm';
import { DrugIndicationSelector } from '@/components/panels/DrugIndicationSelector';
import { StrategyInfusion } from '@/components/panels/StrategyInfusion';
import { SpecialPopulationSelector } from '@/components/panels/SpecialPopulationSelector';
import { DerivedValuesPanel } from '@/components/panels/DerivedValuesPanel';
import { SafetyBanner } from '@/components/panels/SafetyBanner';
import { RegimenResults } from '@/components/panels/RegimenResults';
import { NomogramPlot } from '@/components/visualization/NomogramPlot';
import { ConcentrationCurve } from '@/components/visualization/ConcentrationCurve';
import { EquationsView } from '@/components/collapsibles/EquationsView';
import { RationaleView } from '@/components/collapsibles/RationaleView';
import { AdvancedNomogram } from '@/components/collapsibles/AdvancedNomogram';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { FirstRunNudge } from '@/components/shared/FirstRunNudge';
import { TabGroup } from 'calc-nav';
import type { TabDef } from 'calc-nav';

/**
 * Primary screen: two-column grid (SCREENMAP §1).
 * Left column: inputs (patient, drug, strategy, special pop).
 * Right column: results organized into tabs (Results / Charts / Details)
 * via calc-nav TabGroup with URL hash persistence.
 * Monitoring lives on the Levels screen per SCREENMAP.
 */
export function CalculatorScreen(): JSX.Element {
  const resultsTabs: TabDef[] = [
    {
      id: 'results',
      label: 'Results',
      content: (
        <>
          <ErrorBoundary label="Regimen results">
            <RegimenResults />
          </ErrorBoundary>
          <ErrorBoundary label="Derived values">
            <DerivedValuesPanel />
          </ErrorBoundary>
        </>
      ),
    },
    {
      id: 'charts',
      label: 'Charts',
      content: (
        <>
          <ErrorBoundary label="Nomogram plot">
            <NomogramPlot />
          </ErrorBoundary>
          <ErrorBoundary label="Concentration curve">
            <ConcentrationCurve />
          </ErrorBoundary>
        </>
      ),
    },
    {
      id: 'details',
      label: 'Details',
      content: (
        <>
          <ErrorBoundary label="Equations view">
            <EquationsView />
          </ErrorBoundary>
          <ErrorBoundary label="Rationale view">
            <RationaleView />
          </ErrorBoundary>
          <ErrorBoundary label="Advanced nomogram">
            <AdvancedNomogram />
          </ErrorBoundary>
        </>
      ),
    },
  ];

  return (
    <div className="screen" data-testid="calculator-screen">
      <Breadcrumb />
      <FirstRunNudge />
      <ErrorBoundary label="Safety banner">
        <SafetyBanner />
      </ErrorBoundary>
      <div className="calc-grid">
        <div className="calc-col calc-inputs">
          <ErrorBoundary label="Patient form">
            <PatientForm />
          </ErrorBoundary>
          <ErrorBoundary label="Drug & indication">
            <DrugIndicationSelector />
          </ErrorBoundary>
          <ErrorBoundary label="Strategy">
            <StrategyInfusion />
          </ErrorBoundary>
          <ErrorBoundary label="Special population">
            <SpecialPopulationSelector />
          </ErrorBoundary>
        </div>
        <div className="calc-col calc-results">
          <TabGroup
            section="calc"
            stickyTop="calc(var(--amg-header-height) + var(--amg-tabbar-height) + var(--amg-space-4))"
            tabs={resultsTabs}
          />
        </div>
      </div>
    </div>
  );
}
